import { useCustomViewBlockContext } from '@lighthouse/block'
import { ImageBlock } from '@lighthouse/block/src/blocks'
import type { FileValue } from '@lighthouse/core'
import { type ImageBlockAbstract } from '@lighthouse/core'
import { getFileNameByUrl, getFileSizeByUrl, getFileTypeByUrl } from '@lighthouse/shared'
import { safeToArray } from '@lighthouse/tools'
import React, { useMemo } from 'react'

import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface ImageBlockControllerProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onClick'> {
    blockData: ImageBlockAbstract
    onBlockChange?: (values: ImageBlockAbstract, origin: ImageBlockAbstract) => Promise<void> | void
}

const ImageBlockController: React.FC<ImageBlockControllerProps> = ({ blockData, onBlockChange, ...rest }) => {
    const {
        config: { imageSource, sourceType, sources },
        id
    } = blockData

    const { handleActionListTrigger } = useActionTrigger(id)

    const { record: viewRecord } = useCustomViewBlockContext()
    const parseVariable = useParseVariableWithSubscription(imageSource)

    const fileList = useMemo(() => {
        if (sourceType === 'field') {
            if (!imageSource) {
                return []
            }

            const cellValue = parseVariable(imageSource, { viewRecord })

            const fileValue: FileValue = safeToArray(cellValue?.value)
            return (
                fileValue?.map(item => {
                    const name = getFileNameByUrl(item) || ''
                    const type = getFileTypeByUrl(item)
                    const size = getFileSizeByUrl(item)
                    return { name, size, url: item, type, title: '', link: '' }
                }) ?? []
            )
        }
        return sources || []
    }, [imageSource, parseVariable, sourceType, sources, viewRecord])

    return (
        <ImageBlock
            showEmpty
            blockData={blockData}
            value={fileList}
            onActionTrigger={handleActionListTrigger}
            onBlockChange={onBlockChange}
            {...rest}
        />
    )
}

export default ImageBlockController
